import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { Grid, Typography } from '@mui/material'
import { red } from '@mui/material/colors'
import { BankAccountStatus, IUserBankAccount, UpdateUserBankAccountStatusRequestDto } from '@sparling/financial-common'
import { Modal } from '../../../../components/Modal'
import { createEnum } from '../../../../utils/createEnum'
import { InputSelect } from '../../../../components/InputSelect'
import { InputField } from '../../../../components/InputField'
import { updateUserBankAccountStatusRequest } from '../../../../store/actions'

interface IEditBankAccountStatusModal {
  isOpen: boolean
  modalClose: () => void
  fields: IUserBankAccount
}

const formFields = [
  { type: 'select', label: 'Status', name: 'status', enums: createEnum(BankAccountStatus) },
  {
    type: 'text',
    label: 'Description',
    name: 'statusReason',
    multiline: true,
    rows: 4,
    fullWidth: true,
    withoutPadding: true,
  },
]

export const EditBankAccountStatusModal: FC<IEditBankAccountStatusModal> = ({ isOpen, fields, modalClose }) => {
  const dispatch = useDispatch()

  const formMethods = useForm<UpdateUserBankAccountStatusRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(UpdateUserBankAccountStatusRequestDto),
    defaultValues: useMemo(() => ({ statusReason: '', ...fields }), [fields]),
  })

  const status = useWatch({
    control: formMethods.control,
    name: 'status',
  })

  const statusReason = useWatch({
    control: formMethods.control,
    name: 'statusReason',
  })

  const isSubmitDisabled = useMemo(() => {
    if (status === BankAccountStatus.REJECTED && !statusReason?.trim()) {
      return true
    }
    return !formMethods.formState.isValid
  }, [status, statusReason, formMethods.formState.isValid])

  const { handleSubmit, reset, setValue } = formMethods

  const isFirstRender = React.useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    setValue('statusReason', '')
  }, [status, setValue])

  const formSubmitHandle = useCallback(
    (data: UpdateUserBankAccountStatusRequestDto) => {
      const payload = {
        id: fields.id,
        ...data,
        statusReason: data.statusReason?.trim() || ' ',
      }
      dispatch(updateUserBankAccountStatusRequest(payload))
      reset()
      modalClose?.()
    },
    [dispatch, modalClose, reset, fields],
  )

  return (
    <Modal
      title={'Edit Bank Account Status'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(formSubmitHandle)}
      submitTitle={'Submit'}
      cancelTitle={'Cancel'}
      isSubmitDisabled={isSubmitDisabled}
      width={480}
      submitButtonProps={{
        type: 'button',
      }}
    >
      <FormProvider {...formMethods}>
        <Grid item xs={12} sm={12} mb={6} container direction="column">
          {formFields.map(({ type, label, name, ...props }) =>
            type === 'select' ? (
              <InputSelect
                key={name}
                labelName={label}
                inputName={name}
                placeholder="Please Select"
                fullWidth
                mb={20}
                {...props}
              />
            ) : (
              <div key={name} style={{ width: '100%', position: 'relative' }}>
                <InputField labelName={label} inputName={name} inputType="text" fullWidth {...props} />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-70px',
                    left: 0,
                    right: 0,
                    height: '20px',
                    pointerEvents: 'none',
                  }}
                >
                  {status === BankAccountStatus.REJECTED && !statusReason?.trim() && (
                    <Typography variant="caption" color={red[500]} display="block" fontSize={14}>
                      Description is required when status is Rejected
                    </Typography>
                  )}
                </div>
              </div>
            ),
          )}
        </Grid>
      </FormProvider>
    </Modal>
  )
}
